import React, { useCallback, useEffect, useRef, useState } from "react";
import { getReq } from "../utils/request";
import classes from "./commentSection.module.scss";

interface MentionSuggestion {
    userId: number;
    userReferenceNumber: string;
    firstName: string;
    lastName: string | null;
    fullName: string;
    /** Relationship to the record (assignee / lead / watcher …) — scoped picker only. */
    role?: string | null;
}

export interface MentionScope {
    parentType: string;
    parentId: number;
}

interface CommentInputProps {
    value: string;
    onChange: (rawValue: string) => void;
    onSubmit: () => void;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    /**
     * When set, @-candidates come from the record's own roster
     * (`GET comments/mentionable/:parentType/:parentId`, loaded once and
     * filtered client-side, ranked as returned). The global user directory
     * is only consulted when the roster has no match for the typed text.
     */
    mentionScope?: MentionScope;
}

const MAX_SUGGESTIONS = 8;

const toSuggestion = (row: { userId: number; userReferenceNumber: string; firstName: string; lastName: string | null; fullName?: string; role?: string | null }): MentionSuggestion => ({
    userId: row.userId,
    userReferenceNumber: row.userReferenceNumber,
    firstName: row.firstName,
    lastName: row.lastName ?? null,
    fullName: row.fullName || [row.firstName, row.lastName].filter(Boolean).join(" "),
    role: row.role ?? null,
});

const matchesQuery = (user: MentionSuggestion, query: string): boolean => {
    if (!query) return true;
    const q = query.toLowerCase();
    return user.fullName.toLowerCase().includes(q)
        || (user.firstName || "").toLowerCase().startsWith(q)
        || (user.lastName || "").toLowerCase().startsWith(q);
};

/**
 * Rich comment input that renders @mentions as styled tags inline.
 * Stores raw format @[Name](ref:USR-XXX) but displays as styled @Name chips.
 */
const CommentInput: React.FC<CommentInputProps> = ({
    value,
    onChange,
    onSubmit,
    placeholder = "Write a comment... Use @ to mention someone",
    disabled = false,
    autoFocus = false,
    mentionScope,
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionSuggestions, setMentionSuggestions] = useState<MentionSuggestion[]>([]);
    const [mentionIndex, setMentionIndex] = useState(0);
    const isComposingRef = useRef(false);
    /** Roster for `mentionScope` — null until loaded (or when unscoped). */
    const [scopedUsers, setScopedUsers] = useState<MentionSuggestion[] | null>(null);
    const scopedLoadingRef = useRef(false);
    const scopeKey = mentionScope ? `${mentionScope.parentType}:${mentionScope.parentId}` : "";

    /**
     * The raw value this editor last emitted. Anything arriving in `value` that
     * differs from it came from OUTSIDE (an edit being opened, a reset after
     * submit) and has to be written into the contenteditable.
     */
    const lastEmittedRef = useRef<string>("");

    /**
     * Render a raw comment value into the editor.
     *
     * This used to only ever CLEAR (it acted when `value === ""` and did nothing
     * otherwise), so clicking Edit on an existing comment opened an EMPTY box
     * while the parent still held the original text: typing replaced the comment
     * from scratch, and saving without typing silently re-saved the original.
     *
     * Mentions are stored as `@[Name](ref:USR-1)` and have to become chips again,
     * built with createElement + textContent — never innerHTML — so comment text
     * can't inject markup.
     */
    const renderRawValue = useCallback((raw: string) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.innerHTML = "";
        if (!raw) return;

        const pattern = /@\[([^\]]+)\]\(ref:([^)]+)\)/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(raw)) !== null) {
            if (match.index > lastIndex) {
                editor.appendChild(document.createTextNode(raw.slice(lastIndex, match.index)));
            }
            const chip = document.createElement("span");
            chip.className = classes.mentionChip;
            chip.contentEditable = "false";
            chip.dataset.mentionName = match[1];
            chip.dataset.mentionRef = match[2];
            chip.textContent = `@${match[1]}`;
            editor.appendChild(chip);
            lastIndex = pattern.lastIndex;
        }
        if (lastIndex < raw.length) {
            editor.appendChild(document.createTextNode(raw.slice(lastIndex)));
        }
    }, []);

    // Write in any value that did not come from this editor. Guarding on
    // `lastEmittedRef` keeps us from rewriting the DOM on every keystroke, which
    // would collapse the caret to the start of the box.
    useEffect(() => {
        const incoming = value ?? "";
        if (incoming === lastEmittedRef.current) return;
        lastEmittedRef.current = incoming;
        renderRawValue(incoming);
    }, [value, renderRawValue]);

    useEffect(() => {
        if (autoFocus && editorRef.current) {
            editorRef.current.focus();
        }
    }, [autoFocus]);

    // A new record → forget the previous roster.
    useEffect(() => {
        setScopedUsers(null);
        scopedLoadingRef.current = false;
    }, [scopeKey]);

    // Load the record roster the first time the picker opens (once per scope).
    useEffect(() => {
        if (!showMentions || !mentionScope || scopedUsers !== null || scopedLoadingRef.current) return;
        scopedLoadingRef.current = true;
        let cancelled = false;
        (async () => {
            const response = await getReq(`comments/mentionable/${mentionScope.parentType}/${mentionScope.parentId}`);
            if (cancelled) return;
            const rows = response.status === 200 && Array.isArray(response.body) ? response.body : [];
            setScopedUsers(rows.map(toSuggestion));
        })();
        return () => { cancelled = true; };
    }, [showMentions, mentionScope, scopedUsers]);

    // Mention search: roster first (ranked as returned), global directory only
    // when the roster has nothing for the typed text.
    useEffect(() => {
        if (!showMentions) {
            setMentionSuggestions([]);
            return;
        }
        if (mentionScope) {
            if (scopedUsers === null) {
                setMentionSuggestions([]);
                return;
            }
            const matches = scopedUsers.filter((u) => matchesQuery(u, mentionQuery)).slice(0, MAX_SUGGESTIONS);
            if (matches.length > 0 || mentionQuery.length < 1) {
                setMentionSuggestions(matches);
                setMentionIndex(0);
                return;
            }
        }
        if (mentionQuery.length < 1) {
            setMentionSuggestions([]);
            return;
        }
        const timeout = setTimeout(async () => {
            const response = await getReq(`users/search/simple?keyword=${encodeURIComponent(mentionQuery)}`);
            if (response.status === 200 && Array.isArray(response.body)) {
                setMentionSuggestions(response.body.slice(0, MAX_SUGGESTIONS).map(toSuggestion));
                setMentionIndex(0);
            }
        }, 200);
        return () => clearTimeout(timeout);
    }, [mentionQuery, showMentions, mentionScope, scopedUsers]);

    /**
     * Extract raw text from the editor, converting mention spans back to raw format.
     */
    const extractRawValue = useCallback((): string => {
        if (!editorRef.current) return "";
        let raw = "";
        editorRef.current.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                raw += node.textContent || "";
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                if (el.dataset.mentionRef) {
                    raw += `@[${el.dataset.mentionName}](ref:${el.dataset.mentionRef})`;
                } else {
                    raw += el.textContent || "";
                }
            }
        });
        return raw;
    }, []);

    /**
     * Detect @ trigger from current cursor position.
     */
    const detectMentionTrigger = () => {
        const sel = typeof window.getSelection === "function" ? window.getSelection() : null;
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        const container = range.startContainer;
        if (container.nodeType !== Node.TEXT_NODE) {
            setShowMentions(false);
            return;
        }

        const text = container.textContent || "";
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@(\w*)$/);

        if (atMatch) {
            setShowMentions(true);
            setMentionQuery(atMatch[1]);
        } else {
            setShowMentions(false);
        }
    };

    const insertMention = (user: MentionSuggestion) => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount || !editorRef.current) return;

        const range = sel.getRangeAt(0);
        const container = range.startContainer;
        if (container.nodeType !== Node.TEXT_NODE) return;

        const text = container.textContent || "";
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const atIndex = textBeforeCursor.lastIndexOf("@");

        // Create the mention span
        const mentionSpan = document.createElement("span");
        mentionSpan.className = classes.mentionChip;
        mentionSpan.contentEditable = "false";
        mentionSpan.dataset.mentionRef = user.userReferenceNumber;
        mentionSpan.dataset.mentionName = user.fullName;
        mentionSpan.textContent = `@${user.fullName}`;

        // Split the text node
        const beforeText = text.substring(0, atIndex);
        const afterText = text.substring(cursorPos);

        const beforeNode = document.createTextNode(beforeText);
        const afterNode = document.createTextNode(" " + afterText);

        const parent = container.parentNode!;
        parent.insertBefore(beforeNode, container);
        parent.insertBefore(mentionSpan, container);
        parent.insertBefore(afterNode, container);
        parent.removeChild(container);

        // Move cursor after the mention
        const newRange = document.createRange();
        newRange.setStart(afterNode, 1);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);

        setShowMentions(false);
        emitChange();
    };

    /** Emit, remembering what we sent so the sync effect leaves the caret alone. */
    const emitChange = useCallback(() => {
        const raw = extractRawValue();
        lastEmittedRef.current = raw;
        onChange(raw);
    }, [extractRawValue, onChange]);

    const handleInput = () => {
        if (isComposingRef.current) return;
        detectMentionTrigger();
        emitChange();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (showMentions && mentionSuggestions.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIndex((prev) => Math.min(prev + 1, mentionSuggestions.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                insertMention(mentionSuggestions[mentionIndex]);
            } else if (e.key === "Escape") {
                setShowMentions(false);
            }
            return;
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
        }
    };

    const isEmpty = !value || value.trim() === "";

    return (
        <div className={classes.textareaWrapper}>
            <div
                ref={editorRef}
                className={`${classes.commentEditor} ${isEmpty ? classes.editorEmpty : ""}`}
                contentEditable={!disabled}
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => { isComposingRef.current = true; }}
                onCompositionEnd={() => { isComposingRef.current = false; handleInput(); }}
                data-placeholder={placeholder}
                role="textbox"
            />
            {showMentions && mentionSuggestions.length > 0 && (
                <div className={classes.mentionDropdown} role="listbox">
                    {mentionSuggestions.map((user, idx) => (
                        <div
                            key={user.userId}
                            role="option"
                            aria-selected={idx === mentionIndex}
                            className={`${classes.mentionOption} ${idx === mentionIndex ? classes.mentionOptionActive : ""}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                insertMention(user);
                            }}
                        >
                            <span>{user.fullName}</span>
                            {user.role && <span className={classes.mentionRole}>{user.role}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CommentInput;
